/**
 * DataGridCellSelect — Inline dropdown cell variant
 *
 * Renders a Radix Select that opens immediately on mount.
 * Commits on value change. Cancels on Escape.
 */

import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DataGridCellVariantProps } from './types';

export function DataGridCellSelect({
  value,
  onCommit,
  onCancel,
  onStartEditing,
  saveState,
  options = [],
}: DataGridCellVariantProps) {
  const [open, setOpen] = useState(false);
  const mountedRef = useRef(false);

  // Open dropdown on mount
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      // Small delay to let the component render before opening
      const timer = setTimeout(() => {
        setOpen(true);
        onStartEditing();
      }, 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = (newValue: string) => {
    if (newValue !== String(value ?? '')) {
      onCommit(newValue);
    } else {
      onCancel();
    }
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Closed without selecting — cancel
      onCancel();
    }
  };

  return (
    <div
      className="relative flex items-center w-full h-full"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        } else if (e.key !== 'Tab') {
          e.stopPropagation();
        }
      }}
    >
      <Select
        value={String(value ?? '')}
        onValueChange={handleValueChange}
        open={open}
        onOpenChange={handleOpenChange}
      >
        <SelectTrigger className="h-full border-none shadow-none bg-transparent text-sm px-1 focus:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {saveState.status === 'saving' && (
        <Loader2 className="absolute top-0.5 right-0.5 w-3 h-3 text-muted-foreground animate-spin" />
      )}
    </div>
  );
}
